import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import InstallerService from 'service/InstallerService';
import { InstallerCreateReq } from 'domain/payload/request/installer/InstallerCreateReq';
import { InstallerUpdateReq } from 'domain/payload/request/installer/InstallerUpdateReq';
import LoadingSpinner from 'component/LoadingSpinner';
import { encryptPassword } from 'helpers/EncryptPassword';
import UserService from 'service/UserService';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import { hasSuspensionAccess } from 'helpers/hasPrivilege';

const InstallerForm: React.FC = () => {

  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);

  const navigate = useNavigate();

  const installerService = InstallerService();
  const userService = UserService();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<string[]>([]);

  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);

  const [roleOptions, setRoleOptions] = useState<{id: string; code: string; name: string }[]>([]);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const [suspensionDetail, setSuspensionDetail] = useState<any>(null);
  const [isLoadingSuspension, setIsLoadingSuspension] = useState(false);
  const [isSuspensionProceeding, setIsSuspensionProceeding] = useState(false);
  
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [revokeReason, setRevokeReason] = useState('');

  const isSuspended = suspensionDetail?.status === true;
  const canSuspend = hasSuspensionAccess(profile, "create");

  const handleAutoGenerateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoGeneratePassword(checked);
    
    if (checked) {
      setInitialPassword('');
      setConfirmPassword('');
      setSendInvitationEmail(true);
    } else {
      setSendInvitationEmail(false);
    };
  };

  const handleSendInvitationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (autoGeneratePassword && !e.target.checked) {
      return;
    }

    setSendInvitationEmail(e.target.checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      alert("Please fill in email");
      return;
    }

    if (role.length === 0) {
      alert("Please select at least one role");
      return;
    }

    if (id === "0" && !autoGeneratePassword) {
      if (!initialPassword || !confirmPassword) {
        alert("Please fill in password fields");
        return;
      }
      
      if (initialPassword !== confirmPassword) {
        alert("Please check input confirm password");
        return;
      }
      
      if (initialPassword.length < 12) {
        alert("Password must be at least 12 characters long");
        return;
      }
    }

    setIsLoadingSave(true);

    try {
      if (id === "0") {
        await installerCreate();
        alert(`Data ${email} successfully created!`);
        navigate("/installer");
      } else {
        await installerUpdate();
        alert(`Data ${email} successfully updated!`);
        navigate("/installer");
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchRoleList = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingRoles(true);
    try {
      const res = await userService.userRole(token);
      const options = res.data?.filter((item: any) =>
        item.name.toLowerCase().startsWith("retail")
      ).map((item: any) => ({
        id: item.id, 
        code: item.code || item.id,
        name: item.name,
      })) ?? [];
      
      setRoleOptions(options);
    } catch (err: any) {
      console.error("Error fetching role list:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil daftar role';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {
      const res = await installerService.installerDetail(token, id ?? "0");
      console.log(res);
      setEmail(res.data?.email!);
      setName(res.data?.name || '');
      setPhone(res.data?.phone || '');
      
      if (res.data?.roles) {
        setRole(res.data.roles.map((r: any) => r.id));
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail installer';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchSuspensionDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id || id === '0') {
      if (!token) window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }
    setIsLoadingSuspension(true);
    try {
      const res = await userService.userSuspensionDetail(token, id);
      setSuspensionDetail(res.data);
    } catch (err: any) {
      console.error('Error fetching suspension detail:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail suspension';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSuspension(false);
    }
  };

  const handleSuspensionProceed = async (action: 'suspend' | 'revoke') => {
    const token = profile?.data.token;
    if (!token || !id) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsSuspensionProceeding(true);
    try {
      await userService.userSuspensionProceed(token, {
        userId: id,
        action,
        reason: action === 'suspend' ? suspendReason : revokeReason,
      });
      alert(`User successfully ${action === 'suspend' ? 'suspended' : 'revoked'}.`);
      setSuspendReason('');
      setRevokeReason('');
      setShowSuspendModal(false);
      setShowRevokeModal(false);
      await fetchSuspensionDetail();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || `Terjadi kesalahan saat ${action === 'suspend' ? 'menangguhkan' : 'mencabut suspension'} user`;
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsSuspensionProceeding(false);
    }
  };

  const installerCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const request: InstallerCreateReq = {
      email: email,
      name: name,
      phone: phone,
      initialPassword: autoGeneratePassword ? "" : encryptPassword(initialPassword),
      roleIds: role,
      autoGenerate: autoGeneratePassword,
      sendInvitationEmail: sendInvitationEmail
    };

    await installerService.installerCreate(token, request);
  };

  const installerUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const request: InstallerUpdateReq = {
      id: id!,
      name: name,
      email: email,
      phone: phone,
      roleIds: role 
    };

    await installerService.installerUpdate(token, request);
  };

  useEffect(() => {
    fetchRoleList();
    if (id !== "0") {
      fetchDetail();
      fetchSuspensionDetail();
    }
  }, []);

  const computedOptions = roleOptions.map(r => ({
    ...r,
    code: r.id
  }));

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Field User" : "Edit Field User"}</h4>
        <div className="p-4" style={{ background: 'white' }}>

          {
            isLoadingDetail || isLoadingRoles ?
              <LoadingSpinner />
              :
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Account Information</h5>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                        <input
                          type="email"
                          id="email"
                          className="form-control form-control-compact"
                          value={email}
                          placeholder="Email"
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="roles" className="form-label fw-semibold">Roles <span className="text-danger">*</span></label>
                        <MultiSelectDropdown
                          options={computedOptions}
                          value={role}
                          onChange={setRole}
                          placeholder="Select Roles"
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-semibold">Name <span className="text-muted fw-normal">(Optional)</span></label>
                        <input
                          type="text"
                          id="name"
                          className="form-control form-control-compact"
                          value={name}
                          placeholder="Name"
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label fw-semibold">Phone <span className="text-muted fw-normal">(Optional)</span></label>
                        <input
                          type="text"
                          id="phone"
                          className="form-control form-control-compact"
                          value={phone}
                          placeholder="Phone"
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Credentials</h5>
                    </div>
                    
                    {id === "0" && (
                      <>
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="autoGeneratePassword"
                              checked={autoGeneratePassword}
                              onChange={handleAutoGenerateChange}
                            />
                            <label className="form-check-label" htmlFor="autoGeneratePassword">
                              Auto Generate Password
                            </label>
                          </div>
                        </div>

                        {!autoGeneratePassword && (
                          <>
                            <div className="mb-3">
                              <label htmlFor="initialPassword" className="form-label fw-semibold">Initial Password</label>
                              <input
                                type="password"
                                id="initialPassword"
                                className="form-control form-control-compact"
                                value={initialPassword}
                                placeholder="Initial Password"
                                onChange={(e) => setInitialPassword(e.target.value)}
                                required={!autoGeneratePassword}
                                minLength={12}
                              />
                            </div>
                            <div className="mb-3">
                              <label htmlFor="confirmPassword" className="form-label fw-semibold">Confirm Password</label>
                              <input
                                type="password"
                                id="confirmPassword"
                                className="form-control form-control-compact"
                                value={confirmPassword}
                                placeholder="Confirm Password"
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required={!autoGeneratePassword}
                                minLength={12}
                              />
                            </div>
                          </>
                        )}

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="sendInvitationEmail"
                              checked={sendInvitationEmail}
                              onChange={handleSendInvitationChange}
                            />
                            <label className="form-check-label" htmlFor="sendInvitationEmail">
                              Send Invitation Email
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-end">
                  <Link to="/installer">
                    <button className="btn btn-outline-primary">Cancel</button>
                  </Link>
                  <button type="submit" className="btn btn-primary d-flex justify-content-center align-items-center ms-2" disabled={isLoadingSave}>
                    {
                      isLoadingSave &&
                      <>
                        <LoadingSpinner reverse={true} />
                        <p className="ms-2"></p>
                      </>
                    }
                    Save
                  </button>
                </div>
              </form>
          }
        </div>

        {id !== '0' && (
          <div className="p-4" style={{ background: 'white' }}>
            {isLoadingSuspension ? (
              <LoadingSpinner />
            ) : (
              <div className="row">
                <div className="col-sm-6">
                  <h5 className="mb-4">Suspension Information</h5>

                  <div className="mb-3">
                    <p className="fw-semibold mb-1">Suspension Status</p>
                    {isSuspended ? (
                      <span className='text-danger' style={{ fontWeight: 600 }}>Suspended</span>
                    ) : (
                      <span className='text-success' style={{ fontWeight: 600 }}>User Active</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <p className="fw-semibold mb-1">Suspension Reason</p>
                    <p className="mb-0" style={{ color: '#333' }}>
                      {suspensionDetail?.reason && suspensionDetail.reason !== ''
                        ? suspensionDetail.reason
                        : '-'}
                    </p>
                  </div>

                  {canSuspend && (
                    <button
                      type="button"
                      className="btn"
                      style={{
                        background: isSuspended ? '#6c757d' : '#f4645f',
                        color: 'white',
                        borderRadius: '8px',
                      }}
                      onClick={() => isSuspended ? setShowRevokeModal(true) : setShowSuspendModal(true)}
                    >
                      {isSuspended ? 'Remove Suspend' : 'Suspend Now'}
                    </button>
                  )}
                </div>

                <div className="col-sm-6">
                  <h5 className="mb-4">Suspension History</h5>
                  {suspensionDetail?.history && suspensionDetail.history.length > 0 ? (
                    <table className="table table-borderless" style={{ fontSize: '14px' }}>
                      <thead>
                        <tr style={{ color: '#adb5bd' }}>
                          <th className="ps-0">Suspend At</th>
                          <th>Reason</th>
                          <th>Action</th>
                          <th>Action By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {suspensionDetail.history.map((item: any, index: number) => (
                          <tr key={index}>
                            <td className="ps-0">{item.actionAt}</td>
                            <td>{item.reason || '-'}</td>
                            <td>{item.actionType?.toLowerCase()}</td>
                            <td>{item.actionBy}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ color: '#adb5bd' }}>No suspension history found.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showSuspendModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Suspend User</h5>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">Please provide a reason for suspending this user.</p>
                <textarea
                  className="form-control"
                  placeholder="Write your reason here..."
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => { setShowSuspendModal(false); setSuspendReason(''); }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  disabled={suspendReason.trim() === '' || isSuspensionProceeding}
                  onClick={() => handleSuspensionProceed('suspend')}
                >
                  {isSuspensionProceeding ? <LoadingSpinner reverse={true} /> : 'Confirm Suspend'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRevokeModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowRevokeModal(false)}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Remove Suspend</h5>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">Please provide a reason for revoking this user's suspension.</p>
                <textarea
                  className="form-control"
                  placeholder="Write your reason here... (optional)"
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => { setShowRevokeModal(false); setRevokeReason(''); }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={isSuspensionProceeding}
                  onClick={() => handleSuspensionProceed('revoke')}
                >
                  {isSuspensionProceeding ? <LoadingSpinner reverse={true} /> : 'Confirm Revoke'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallerForm;